import photoView from '../../photoView/index.js';

export default (route, params, options) => ({
    ...photoView(route, params, options),

    selectTile(tileIndex) {
        let tile = this.tilesState[`${tileIndex}`];

        if (this.options.maxSelection >= 1) {
            this.handleMaxSelection(tile, tileIndex);

            return;
        }
    },

    initHandleScroll() {
        this.scrollDirection = 'down';
        let lastScrollY = this.container.scrollY;
        let lastTime = Date.now();
        let scrollTimeout;

        const calculateDelay = (speed) => {
            return Math.max(20, Math.min(100, speed * 100));
        };

        const handleScrollStop = () => {
            this.loadSectionData();
        };

        const removeOldSections = () => {
            let sectionsDivs = this.gridScrollNode.querySelectorAll('div.section');

            while (sectionsDivs.length > this.config.removeOldSectionAfter) {
                const indexToRemove = this.scrollDirection === 'down' ? 0 : sectionsDivs.length - 1;

                const sectionToRemove = sectionsDivs[indexToRemove];
                const sectionId = this.splitId(sectionToRemove.id);

                this.cancelPendingImageRequests(sectionToRemove);

                this.sections[sectionId].shouldMountEmptyState = true;
                this.sections[sectionId].shouldUpdate = true;

                this.gridScrollNode.removeChild(sectionToRemove);
                sectionsDivs = this.gridScrollNode.querySelectorAll('div.section');
            }
        };

        const reorderGrid = () => {
            const gridItems = Array.from(this.gridScrollNode.children);

            gridItems.sort((a, b) => {
                const idA = parseInt(a.id.replace(/\D/g, ''), 10);
                const idB = parseInt(b.id.replace(/\D/g, ''), 10);

                return idA - idB;
            });

            gridItems.forEach((item) => this.gridScrollNode.appendChild(item));
        };

        this.container.addEventListener(
            'scroll',
            async () => {
                const currentY = this.container.scrollTop;
                const distance = Math.abs(currentY - lastScrollY);
                this.scrollDirection = currentY > lastScrollY ? 'down' : 'up';

                const currentTime = Date.now();
                const timeDiff = currentTime - lastTime || 1;
                const speed = distance / timeDiff;

                lastScrollY = currentY;
                lastTime = currentTime;

                const delay = calculateDelay(speed);

                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(handleScrollStop, delay);

                await this.loadSectionEmpty();
                removeOldSections();
            },
            { passive: true },
        );
    },

    tileStateObject(image, sectionId = null, segmentId = null) {
        const selected = this.selectedTiles.includes(image.id) || this.allSelected;

        return {
            id: image.id,
            name: image.full_name,
            event_id: image.event_id ?? null,
            watermark_preview: image?.preview ?? null,
            preview: image.preview ?? null,
            deleted: image.deleted_at ?? null,
            teams: image.teams ?? [],
            tags: image.teams?.map((team) => team.id) ?? [],
            selected,
            segmentId,
            sectionId,
        };
    },

    setSelectedTilesById(selectedIds) {
        selectedIds.forEach((id) => {
            const element = document.querySelector(`[data-image-id="${id}"]`);

            this.selectTile(element.parentElement.dataset.tileId);
        });
    },

    removeSelectedTilesById(id) {
        const element = document.querySelector(`[data-image-id="${id}"]`);

        this.selectTile(element.parentElement.dataset.tileId);
    },

    handleMaxSelection(tile, tileIndex) {
        const element = document.querySelector(`[data-tile-id="${tileIndex}"]`);

        if (tile.selected) {
            tile.selected = false;
            element.classList.remove('selected');

            this.selectedTiles = this.selectedTiles.filter((id) => id !== tile.id);
        } else {
            if (this.reachedSelectionLimit()) {
                return;
            }

            tile.selected = true;
            element.classList.add('selected');

            this.selectedTiles.push(tile.id);
        }

        this.totalTilesCount = this.selectedTiles.length;

        let reachedLimit = this.reachedSelectionLimit();

        this.$nextTick(() => {
            this.$el.querySelectorAll('.box-image:not(.selected)').forEach((box) => {
                box.classList.toggle('cursor-not-allowed', reachedLimit);
                box.classList.toggle('cursor-auto', !reachedLimit);
            });
        });

        window.dispatchEvent(
            new CustomEvent('tile-selected', {
                detail: {
                    selectedIds: this.selectedTiles,
                    totalTilesCount: this.totalTilesCount,
                },
            }),
        );
    },

    populateSection(sectionDiv, segments) {
        const fragment = document.createDocumentFragment();

        const segmentDivTemplate = document.createElement('div');
        segmentDivTemplate.className = 'segment grid';
        segmentDivTemplate.style.cssText = `
        width: ${this.config.containerWidth}px;
        height: ${this.config.segmentRowHeight}px;
                grid-template-columns: repeat(${this.segmentSize}, 1fr);
            `;

        for (const segment of segments) {
            const segmentDiv = segmentDivTemplate.cloneNode(true);
            segmentDiv.id = `photo-view-segment-${segment.segmentId}`;

            segmentDiv.append(...this.generateTiles(segment));

            fragment.appendChild(segmentDiv);
        }

        requestAnimationFrame(() => {
            sectionDiv.appendChild(fragment);
            this.addSelectClick(sectionDiv);
            this.addFetchPriorityObserver(sectionDiv);
        });
    },

    *generateTiles({ images: tiles, segmentId }) {
        for (const [index, tile] of tiles.entries()) {
            yield this.getTileElement(tile, `${segmentId}${index + 1}`);
        }
    },

    getTileElement(image, index) {
        const tileDiv = this.tileTemplate.cloneNode(true);

        tileDiv.style.width = `${this.tileSize.width}px`;
        tileDiv.style.height = `${this.tileSize.height}px`;
        tileDiv.setAttribute(
            'x-bind:class',
            `{
            'bg-gray-700': true,
            'border-green-500': isSelected('${index}'),
            'border-transparent': !isSelected('${index}'),
            'cursor-not-allowed opacity-50 pointer-events-none': reachedSelectionLimit() && !isSelected('${index}'),
        }`,
        );

        tileDiv.setAttribute('data-tile-id', index);

        const img = tileDiv.querySelector('img');
        img.src = image.thumb;
        img.setAttribute('data-image-id', image.id);

        tileDiv.insertAdjacentHTML('beforeend', this.fillActionsAndTags(image, index));

        return tileDiv;
    },

    fillActionsAndTags(image, index) {
        return `${this.topImageItems(image, index)}${this.bottomImageItems(image, index)}`;
    },

    reachedSelectionLimit() {
        const maxSelection = _.get(this.options, 'maxSelection', null);

        if (maxSelection && this.selectedTiles.length >= maxSelection) {
            return true;
        }

        return false;
    },

    openQuickBuyPreview(index) {
        let tile = this.tilesState[`${index}`];

        const position = Object.values(this.tilesState).findIndex(item => item.id === tile.id);

        this.$dispatch('open::quick-buy-preview', [
            this.tilesState,
            position,
            {
                canDownload: false,
                canSelect: true,
                imageAttr: 'preview',
                watermark: tile?.preview ?? null,
            }
        ]);
    },

    addImageButton(image, index) {
        return `
            <div
                x-cloak
                x-show="isSelected('${index}')"
                x-on:click.stop="selectTile('${index}')"
                class="flex items-center p-1 rounded-full cursor-pointer bg-red-500"
                >
                <svg class="size-5 text-white" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>

            <div
                x-cloak
                x-show="!isSelected('${index}') && !reachedSelectionLimit()"
                x-on:click.stop="selectTile('${index}')"
                class="flex items-center p-1 rounded-full cursor-pointer bg-white">
                <span class="
                        uppercase font-bold text-[10px] tracking-[0.5px]
                        text-gray-600 pt-0.5 mr-0.5 ml-1.5
                    ">
                    Add to Package
                </span>
                <svg class="size-5 text-gray-500" x-ref="unsavedIcon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
            </div>
        `;
    },

    bottomImageItems(image, index) {
        const imageNameBadge = this.options.imageNameBadge ? `
            <span class="text-2xs h-6 gap-1 flex items-center text-gray-900 font-semibold py-0.5 bg-white px-3 rounded-xl -mb-1 -ml-1">
              ${image.name} 
            </span>
        ` : this.addImageButton(image, index);

        return `
            <div class="buttons absolute bottom-0 w-full h-10 flex items-center px-2 right-0 justify-end">
              ${this.teamTagsGroup(image)}
              ${imageNameBadge}
            </div>
        `;
    },

    addSelectClick(sectionDiv) {
        sectionDiv.querySelectorAll('div[data-tile-id]').forEach((el) => {
            el.addEventListener('click', () =>
                this.openQuickBuyPreview(el.dataset.tileId)
            );
        });
    },
});